import {isEmpty} from 'lodash';
import {useCallback, useEffect} from 'react';

import {settingsApi} from 'api';
import {useApiData, useObservable} from 'hooks';
import {AccountAssignmentInfo, CustomField} from 'interfaces';
import {guestUserInfoSchemaStore, Observable, userInfoSchemaStore} from 'services';
import {AssignmentData, InviteeParticipantRole} from 'types';
import {userInfoUtils} from 'utils';

type BaseProps = {
    fetchOnInit?: boolean;
    store: Observable<AssignmentData<CustomField[]>[]>
    apiMethod: () => Promise<AssignmentData<CustomField[]>[]>
    enabled?: boolean
}

function useBaseUserInfoSchema({fetchOnInit, enabled, store, apiMethod}: BaseProps) {
    const userInfoSchemaData = useObservable(store);

    const onSuccess = useCallback((data) => {
        store.setValue(data);
    }, [store]);

    const {send: fetchUserInfoSchemaFn, error, loadedAt, loading} = useApiData(apiMethod, {onSuccess});

    const fetchUserInfoSchema = useCallback(() => {
        if (enabled) {
            return fetchUserInfoSchemaFn();
        }

        return Promise.resolve();
    }, [enabled, fetchUserInfoSchemaFn]);

    const getForAssignment = useCallback((assignment: AccountAssignmentInfo) =>
        userInfoUtils.getAssignmentUserInfoFields(userInfoSchemaData, assignment), [userInfoSchemaData]);

    const getUserInfoFields = useCallback((assignment: AccountAssignmentInfo) => {
        const userInfoFields = getForAssignment(assignment);
        return userInfoUtils.getUserInfoFields(userInfoFields);
    }, [getForAssignment]);

    const getControlOnlyFields = useCallback((assignment: AccountAssignmentInfo) => {
        const userInfoFields = getForAssignment(assignment);
        return userInfoUtils.getControlOnlyFields(userInfoFields);
    }, [getForAssignment]);

    useEffect(() => {
        if (fetchOnInit && isEmpty(userInfoSchemaData) && !loadedAt && !error) {
            fetchUserInfoSchema();
        }
    }, [fetchOnInit, loadedAt, fetchUserInfoSchema, userInfoSchemaData, error]);

    return {
        fetchUserInfoSchema,
        loading,
        getUserInfoFields,
        getControlOnlyFields,
        userInfoSchemaData
    };
}

type Props = {
    role?: InviteeParticipantRole;
    fetchOnInit?: boolean;
    guestEnabled?: boolean;
}

export function useUserInfoSchema({
    role,
    fetchOnInit,
    guestEnabled
}: Props = {role: InviteeParticipantRole.Client, fetchOnInit: true, guestEnabled: false}) {
    const clientData = useBaseUserInfoSchema({
        fetchOnInit,
        store: userInfoSchemaStore,
        apiMethod: settingsApi.getUserInfoSchema,
        enabled: true
    });

    const guestData = useBaseUserInfoSchema({
        fetchOnInit,
        store: guestUserInfoSchemaStore,
        apiMethod: settingsApi.getGuestUserInfoSchema,
        enabled: guestEnabled
    });

    if (role === InviteeParticipantRole.Guest && guestEnabled) {
        return guestData;
    }

    return clientData;

}